import { useCallback, useEffect, useMemo } from 'react';

import { useQuery, useQueryClient } from 'react-query';
import { shallow } from 'zustand/shallow';

import {
  CLASSIC_EXERCISE_STALE_TIME_MS,
  classicExerciseQueryFn,
  classicExerciseQueryKey,
} from 'api/rest/exercises/classicExerciseQuery';
import { exerciseIdForRestApi } from 'api/rest/exercises/exerciseIdForRestApi';
import { useGlobalDialogContext } from 'context/GlobalDialogContext';
import { useUserLogContext } from 'context/UserLogContext/UserLogContext';
import useFeatureChecker from 'services/common/useFeatureChecker/useFeatureChecker';
import useUserPreferencesStore from 'storages/userPreferences';

const mediaUrl = import.meta.env.VITE_MEDIA_URL;

const prefetchExerciseMedia = exercise => {
  if (mediaUrl && exercise?.files?.length) {
    exercise.files.forEach(file => {
      if (!file.croppedPngFile) return;
      const image = new Image();
      image.src = `${mediaUrl}${file.croppedPngFile}`;
    });
  }
  if (exercise?.audio) {
    const audioEl = new Audio();
    audioEl.preload = 'auto';
    audioEl.src = exercise.audio;
  }
};

const prefetchNextExercise = (queryClient, exercises, exerciseCursor) => {
  const nextId = exerciseIdForRestApi(exercises?.[exerciseCursor + 1]?.id);
  if (!nextId) return;

  const cached = queryClient.getQueryData(classicExerciseQueryKey(nextId));
  prefetchExerciseMedia(cached);

  void queryClient
    .prefetchQuery(classicExerciseQueryKey(nextId), classicExerciseQueryFn(nextId), {
      staleTime: CLASSIC_EXERCISE_STALE_TIME_MS,
    })
    .then(() => {
      prefetchExerciseMedia(queryClient.getQueryData(classicExerciseQueryKey(nextId)));
    });
};

const useNavigation = () => {
  const { logEvent } = useUserLogContext();
  const { useDataStore, isSheet, feature } = useFeatureChecker();
  const { hideMarking } = useUserPreferencesStore();
  const queryClient = useQueryClient();

  const {
    fileType,
    setFileType,
    currentExerciseShape,
    currentExercise,
    exercisesCount,
    exercises,
    exerciseCursor,
    fetched,
    setExerciseCursor,
    allExercisesCompleted,
    mark,
  } = useDataStore(
    s => ({
      fileType: s.fileType,
      setFileType: s.setFileType,
      currentExerciseShape: s.currentExerciseShape,
      currentExercise: s.currentExercise,
      exercisesCount: s.exercisesCount,
      exercises: s.exercises,
      exerciseCursor: s.exerciseCursor,
      fetched: s.fetched,
      setExerciseCursor: s.setExerciseCursor,
      allExercisesCompleted: s.allExercisesCompleted,
      mark: s.mark,
    }),
    shallow,
  );
  const { addToGlobalDialogQueue, GLOBAL_DIALOG } = useGlobalDialogContext();
  const showModalMark = () =>
    addToGlobalDialogQueue({
      type: GLOBAL_DIALOG.MARK_EXERCISE,
      props: {
        exerciseToMark: currentExercise?.id,
        withNext: true,
        initialMark: currentExercise?.userMark?.mark ?? mark ?? null,
        initialIsDone: currentExercise?.userMark?.isDone ?? currentExercise?.isDone ?? null,
      },
    });

  const listExercise = exercises?.[exerciseCursor];
  const nameFromList = listExercise?.importFileIdentifier;
  const restExerciseId = exerciseIdForRestApi(listExercise?.id || currentExercise?.id);
  // Sheets list already includes importFileIdentifier; otherwise share Main/Draw/Note cache.
  const { data: exerciseInfo } = useQuery(
    classicExerciseQueryKey(restExerciseId),
    classicExerciseQueryFn(restExerciseId),
    {
      enabled: !!restExerciseId && !nameFromList,
      staleTime: CLASSIC_EXERCISE_STALE_TIME_MS,
    },
  );

  const buttonsState = useMemo(
    () => ({
      previous: exerciseCursor || allExercisesCompleted,
      next: fetched && currentExercise && exerciseCursor !== exercisesCount - 1,
      answer: exerciseCursor !== exercisesCount && !allExercisesCompleted && fetched && currentExercise,
      hint: currentExerciseShape && currentExerciseShape.p > 0 && !allExercisesCompleted,
      question: !!exercises?.length,
    }),
    [exerciseCursor, fileType, exercisesCount, currentExerciseShape, allExercisesCompleted, fetched],
  );

  const currentName = useMemo(
    () => nameFromList || exerciseInfo?.importFileIdentifier || '',
    [nameFromList, exerciseInfo?.importFileIdentifier],
  );
  const eventData = useMemo(() => ({ feature, exercise: currentExercise?.index }), [feature, currentExercise]);

  useEffect(() => {
    prefetchNextExercise(queryClient, exercises, exerciseCursor);
  }, [queryClient, exercises, exerciseCursor]);

  const previous = () => {
    logEvent('previous-exercise-click', eventData);
    setExerciseCursor('previous');
  };

  const next = () => {
    logEvent('next-exercise-click', eventData);
    setExerciseCursor('next');
  };

  const shouldShowModal = e => {
    if (mark || e?.ctrlKey) return false;
    if (isSheet && hideMarking) return false;
    return true;
  };

  const nextWithMarking = useCallback(
    e => {
      const modal = shouldShowModal(e);
      if (modal) {
        prefetchNextExercise(queryClient, exercises, exerciseCursor);
        showModalMark();
        return;
      }
      next();
    },
    [mark, currentExercise?.id, hideMarking, exercises, exerciseCursor, queryClient],
  );

  const fileTypeChange = useCallback(
    newFileType => () => {
      if (newFileType === 'p') logEvent('hint-click', eventData);
      if (newFileType === 'o') logEvent('answer-click', eventData);
      if (newFileType === 't') logEvent('content-click', eventData);
      setFileType(newFileType);
    },
    [],
  );

  return {
    previous,
    next,
    nextWithMarking,
    buttonsState,
    fileTypeChange,
    currentName,
  };
};

export default useNavigation;
